<template>
  <section class="invoice-preview-wrapper">
    <b-alert v-model="showAlertBanner" variant="primary" dismissible class="mb-2">
      <div class="d-flex align-items-center p-1">
        <feather-icon icon="InfoIcon" size="24" class="mr-1" />
        <div>
          <h5 class="mb-0 font-weight-bolder text-primary">
            {{ $t('Alert Management') }}
          </h5>
          <p class="mb-0 mt-50" style="font-size: 1.05rem;">
            {{ $t('To enable or disable alerts, use the Group option.') }}
            <br>
            <feather-icon icon="StarIcon" size="14" class="mr-25 text-warning" />
            <span class="text-muted">{{ $t('This provides a more efficient way to configure multiple assets at once.') }}</span>
          </p>
        </div>
      </div>
    </b-alert>

    <b-row class="invoice-preview">
      <!-- Col: Left (table container) -->
      <b-col cols="12">
        <transition name="fade" mode="out-in">
          <b-card key="3" v-if="showFilters" no-body class="mb-2 ">
            <b-card-header class="d-flex justify-content-between pl-1 pt-1">
              <h5>
                {{ $t('Filters') }}
              </h5>
              <b-avatar @click="closeFilters()" v-b-tooltip.hover :title="titleTootipBtnCloseFilter" class="mr-1" button
                        variant="danger" size="30px">
                <feather-icon icon="XIcon"/>
              </b-avatar>
            </b-card-header>
            <b-card-body>
              <b-row>
                <b-col cols="12" md="6" class="mb-md-0 mb-2">
                  <h5 class="font-weight-bold">{{ $t('Date Start') }}</h5>
                  <flat-pickr v-model="dateStart" class="form-control" :config="flatPickrConfig" />
                </b-col>
                <b-col cols="12" md="6" class="mb-md-0 mb-2">
                  <h5 class="font-weight-bold">{{ $t('Date End') }}</h5>
                  <flat-pickr v-model="dateEnd" class="form-control" :config="flatPickrConfig" />
                </b-col>
              </b-row>
              <b-row class="mt-md-1">
                <b-col cols="12" md="6" class="mb-md-0 mb-2">
                  <h5 class="font-weight-bold">{{ $t('Alarm') }}</h5>
                  <multiselect v-model="chosenAlert" :options="options" :multiple="true" :close-on-select="false"
                               :clear-on-select="false" :preserve-search="true" :placeholder="placeholderSelect"
                               label="name" track-by="name" :preselect-first="false">
                    <template slot="selection" slot-scope="{ values, search, isOpen }"><span class="multiselect__single"
                                                                                             v-if="values.length && !isOpen">{{
                        values.length
                      }} &nbsp; {{ $t('options selected') }} </span></template>
                  </multiselect>
                </b-col>
                <b-col cols="12" md="6" class="mb-md-0 mb-2 d-flex align-items-center">
                  <b-button
                      @click="fetchAlerts()"
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      class="btn-icon mt-2 mr-1"
                      :title="titleBtnSearch"
                  >
                    <feather-icon icon="SearchIcon"/>
                  </b-button>
                  <b-button 
                      v-if="itens.length > 0" 
                      :disabled="isExporting"
                      @click="exportAlertsExcel()" 
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'" 
                      variant="success" 
                      class="mt-2"
                  >
                      <b-spinner v-if="isExporting" small class="mr-50" />
                      <feather-icon v-else icon="DownloadIcon" class="mr-50" />
                      <span class="align-middle">{{ isExporting ? $t('Exporting...') : $t('Export to Excel') }}</span>
                  </b-button>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </transition>

        <b-card no-body class="invoice-preview-card" :class="transition">
          <table class="table ">
            <thead>
            <tr>
              <th class="style-dark text-center" colspan="3" scope="col">
                <div class="d-flex justify-content-between align-items-center w-100">
                    <span style="font-size: 0.9rem;" v-if="!isLoadingAsset">
                        🎫 {{ $t('Model') }}: <strong class="mr-2">{{ asset.modelIdName }}</strong> {{ $t('Identifier') }}: <strong>{{ asset.plate ? asset.plate : asset.chassisNumber }}</strong>
                    </span>
                    <b-skeleton v-else width="250px"></b-skeleton>
                    
                    <span v-show="!showFilters" class="ml-1">
                        <b-avatar @click="showFilters = true" button rounded="sm" variant="primary"
                                  size="25px" v-b-tooltip.hover :title="titleTootipBtnFilter">
                            <feather-icon icon="FilterIcon"/>
                        </b-avatar>
                    </span>
                </div>
              </th>
            </tr>
            </thead>

            <tbody v-if="isLoading">
                <tr class="row px-1" v-for="n in 5" :key="'skeleton-' + n">
                    <td class="col-sm-12">
                        <b-skeleton animation="wave" width="85%"></b-skeleton>
                        <b-skeleton animation="wave" width="55%"></b-skeleton>
                        <b-skeleton animation="wave" width="70%"></b-skeleton>
                    </td>
                </tr>
            </tbody>
            
            <tbody v-else-if="itens.length > 0">
                <template v-for="item in itens">
                <tr class="row px-1 " :key="'date-' + item.date">
                    <td class="d-flex justify-content-between col-sm-12 day-table"><span
                        class="font-weight-bold">{{ item.dayWeek }}</span> <span class="font-weight-bold">{{
                        item.date
                    }}</span></td>
                </tr>
                <tr class="row px-1" :key="'hours-' + item.date">
                    <template v-for="(hour, index) in item.hours">
                    <td :key="'td-hour-' + index" class=" col-sm-4">{{ formateDate(hour.datetime) }}</td>
                    <td :key="'td-info-' + index" class=" col-sm-8 text-center">
                        <b-badge :variant="resolveVariant(hour.eventTypeId)">
                        <span> {{ hour.eventTypeName }}</span>
                        </b-badge>
                        <b-badge class="ml-1" variant="info"
                                v-if="hour.additionalInfo != null && hour.additionalInfo.length <= 20">
                        <span>{{ hour.additionalInfo }}</span>
                        </b-badge>
                        <p class="mt-1"><span
                            v-if="hour.additionalInfo != null && hour.additionalInfo.length > 20">{{
                            hour.additionalInfo
                        }}</span>
                        </p>
                    </td>
                    </template>
                </tr>
                </template>
            </tbody>
            
            <tbody v-else>
                <tr class="row px-1 ">
                <td class="text-center col-sm-12 day-table">
                    <h5>{{ this.$t('No matching records found') }}</h5>
                </td>
                </tr>
            </tbody>
          </table>
          <!-- Section footer table -->
          <div v-show="itens.length > 0" class="m-2">
            <b-row>
              <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
                <span class="text-muted"> {{ $t('Showing') }} {{
                    from
                  }} {{ $t('to') }} {{ to }} {{ $t('of') }} {{ totalItems }} {{ $t('entries') }} </span>
              </b-col>
              <!-- Pagination -->
              <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
                <b-pagination v-model="currentPage" :total-rows="totalItems" per-page="10" first-number last-number
                              class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18"/>
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18"/>
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-col>

    </b-row>
  </section>
</template>

<script>
import {
  ref,
  watch,
  onMounted,
  onUnmounted
} from '@vue/composition-api'
import store from '@/store'
import alarmsStoreModule from './alarmsStoreModule'
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'
import router from '@/router'
import Ripple from 'vue-ripple-directive'

import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BAlert,
  VBToggle,
  VBTooltip,
  BPagination,
  BBadge,
  BAvatar,
  BSpinner,
  BSkeleton
} from 'bootstrap-vue'

import flatPickr from 'vue-flatpickr-component'
import Multiselect from 'vue-multiselect'

export default {
  directives: {
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
    Ripple
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BAlert,
    BBadge,
    BPagination,
    BAvatar,
    BSpinner,
    BSkeleton,
    flatPickr,
    Multiselect,
  },

  setup(props, { root }) {
    const ALARMS_APP_STORE_MODULE_NAME = 'app-alarms'

    // Register module
    if (!store.hasModule(ALARMS_APP_STORE_MODULE_NAME)) store.registerModule(ALARMS_APP_STORE_MODULE_NAME, alarmsStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(ALARMS_APP_STORE_MODULE_NAME)) store.unregisterModule(ALARMS_APP_STORE_MODULE_NAME)
    })

    const myId = router.currentRoute.params.id

    const showAlertBanner = ref(true)

    const titleTootipBtnFilter = root.$t('Open Filters')
    const titleTootipBtnCloseFilter = root.$t('Close Filters')
    const titleBtnSearch = root.$t('Search')
    const placeholderSelect = root.$t('Pick some')

    const currentPage = ref(1)
    const showFilters = ref(false)

    const itens = ref([])
    const totalItems = ref(0)
    const from = ref(0)
    const to = ref(0)

    const dateStart = ref('')
    const dateEnd = ref('')
    const chosenAlert = ref([])
    const options = ref([])

    const transition = ref('')
    
    const asset = ref({})
    const isLoadingAsset = ref(false)
    const isLoading = ref(false)

    const isExporting = ref(false)
    const flatPickrConfig = ref({
      altFormat: 'd/m/Y',
      altInput: true,
      dateFormat: 'Y-m-d',
      locale: getFlatpickrLocale(),
    })

    const msgDialog = (msg, icon) => {
      root.$swal({
        position: 'top-end',
        icon: icon,
        title: msg,
        showConfirmButton: false,
        timer: 1500 * 3,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      })
    }

    const fetchAsset = () => {
        isLoadingAsset.value = true
        store
            .dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchAsset`, {
                id: myId,
            })
            .then(response => {
                asset.value = response.data
            })
            .catch(() => {})
            .finally(() => {
                isLoadingAsset.value = false
            })
    }

    const closeFilters = () => {
      showFilters.value = false
    }

    const resolveVariant = (typeId) => {
      return 'warning'
    }

    const fetchTypeAlerts = () => {
      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchTypeAlerts`)
        .then(response => {
          options.value = response.data.data
        })
        .catch(() => {})
    }

    const formateDate = (date) => {
      return new Date(date).toLocaleTimeString(navigator.language, {
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const setHour = (date) => {
      var dt = new Date(date).toISOString();
      return `${dt.split('T')[0]}T23:59:59.${dt.split('.')[1]}`
    }

    const alertToIds = (chosenAlertParam) => {
      var ids = []
      chosenAlertParam.map(item => {
        ids.push(item.id)
      })
      return ids
    }

    const getDayWeek = (dt) => {
      const month = dt.split('/')[1]
      const day = dt.split('/')[0]
      const year = dt.split('/')[2]
      const week = new Date(`${month}/${day}/${year}`).getDay()

      switch (week) {
        case 0: return root.$t('Sunday')
        case 1: return root.$t('Monday')
        case 2: return root.$t('Tuesday')
        case 3: return root.$t('Wednesday')
        case 4: return root.$t('Thursday')
        case 5: return root.$t('Friday')
        case 6: return root.$t('Saturday')
      }
    }

    const responseToItem = (alarms) => {
      const groupsObj = alarms.reduce((groups, game) => {
        const date = new Date(game.datetime).toLocaleDateString(getIntlLocale());
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(game);
        return groups;
      }, {});

      return Object.keys(groupsObj).map((date) => {
        const dayWeek = getDayWeek(date)
        return {
          date,
          dayWeek,
          hours: groupsObj[date]
        };
      });
    }

    const fetchAlerts = () => {
      const start = dateStart.value ? new Date(dateStart.value).toISOString() : ''
      const end = dateEnd.value ? setHour(dateEnd.value) : ''
      const alertIds = chosenAlert.value.length > 0 ? alertToIds(chosenAlert.value) : ''

      isLoading.value = true
      itens.value = []

      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchAlerts`, {
        q: '',
        page: currentPage.value,
        assetId: myId,
        alertIds: alertIds,
        start: start,
        end: end
      })
        .then(response => {
          itens.value = responseToItem(response.data.data)
          totalItems.value = response.data.totalItems

          const localItemsCount = itens.value ? response.data.data.length : 0
          const perPage = 10
          from.value = perPage * (currentPage.value - 1) + (localItemsCount ? 1 : 0)
          to.value = perPage * (currentPage.value - 1) + localItemsCount
        })
        .catch(() => {})
        .finally(() => {
            isLoading.value = false
        })
    }

    const exportAlertsExcel = () => {
      const start = dateStart.value ? new Date(dateStart.value).toISOString() : ''
      const end = dateEnd.value ? setHour(dateEnd.value) : ''
      const alertIds = chosenAlert.value.length > 0 ? alertToIds(chosenAlert.value) : ''

      isExporting.value = true

      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/exportAlertsExcel`, {
        q: '',
        page: currentPage.value,
        assetId: myId,
        alertIds: alertIds,
        start: start,
        end: end
      })
        .then(response => {
          const date = new Date().toLocaleDateString()
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `alarms_${date}.xls`);
          document.body.appendChild(link);
          link.click();
        })
        .catch(() => {
          msgDialog(root.$t('Error exporting to Excel'), 'error')
        })
        .finally(() => {
          isExporting.value = false
        })
    }

    watch(currentPage, () => {
      fetchAlerts()
    })

    onMounted(() => {
      fetchAsset()
      fetchTypeAlerts()
      fetchAlerts()
    })

    return {
      titleTootipBtnFilter,
      titleTootipBtnCloseFilter,
      titleBtnSearch,
      placeholderSelect,
      currentPage,
      showFilters,
      itens,
      totalItems,
      from,
      to,
      dateStart,
      dateEnd,
      chosenAlert,
      options,
      transition,
      isExporting,
      flatPickrConfig,
      closeFilters,
      resolveVariant,
      formateDate,
      exportAlertsExcel,
      fetchAlerts,
      showAlertBanner,
      asset,
      isLoadingAsset,
      isLoading
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css">
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.rise {
  transition-duration: .8s;
  transform: translate(0px, -26.5%);
}

.day-table {
  background-color: #f3f2f7;
}

.style-dark {
  background-color: #d3d6dc !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .8s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
